import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import { useDialog } from '@hooks/useDialog';
import {
  ChevronRight, FileText, Users, MapPin, Stethoscope,
  Phone, Mail, Printer, CheckCircle, AlertCircle, Power,
} from 'lucide-react';
import { useClinicData } from '../hooks/useClinicData';
import { ClinicStatus } from '../types/clinic';
import ClinicContactsView from '../components/ClinicContactsView';
import ClinicAddressesTab from '../components/ClinicAddressesTab';
import ClinicInfoSection from '../components/ClinicInfoSection';
import ClinicCategoriesSection from '../components/ClinicCategoriesSection';
import ClinicCapabilitiesSection from '../components/ClinicCapabilitiesSection';
import ClinicOnboardingSection from '../components/ClinicOnboardingSection';
import ClinicExternalSystemsSection from '../components/ClinicExternalSystemsSection';
import OperatingHoursSection from '../components/OperatingHoursSection';
import ClinicProceduresView from '../components/ClinicProceduresView';
import PhoneLink, { PhoneDisplay } from '@shared/components/common/PhoneLink';
import RequirePermission from '@shared/components/RequirePermission';
import './ClinicDetailPage.css';

type TabType = 'info' | 'contacts' | 'addresses' | 'services';

export default function ClinicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const clinicId = Number(id);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('info');
  const verifyModal = useDialog();
  const statusModal = useDialog();

  const {
    clinic,
    isLoading,
    error,
    refetch,
    canVerify,
    verify,
    verifyPending,
    verifyError,
    clearVerifyError,
    setStatus,
    statusPending,
    statusError,
    clearStatusError,
  } = useClinicData(clinicId, {
    onVerifySuccess: verifyModal.close,
    onStatusSuccess: statusModal.close,
  });

  const handleBackToGrid = () => {
    navigate('/clinics?view=grid');
  };

  const handleOpenVerify = () => {
    clearVerifyError();
    verifyModal.open();
  };

  const handleConfirmVerify = () => {
    verify();
  };

  const handleOpenStatus = () => {
    clearStatusError();
    statusModal.open();
  };

  // SLV2-3195: key the toggle off the effective status the API now derives,
  // not the legacy isActive bit — that bit never tracked status changes, so
  // the button was stuck on "Deactivate" and Activate was unreachable.
  // A Draft clinic has never been active, so it too must offer Activate
  // (2026-09-14 DEV pass: Draft showed Deactivate, leaving Activate unreachable).
  const offersActivate = clinic?.status === ClinicStatus.Inactive || clinic?.status === ClinicStatus.Draft;

  const handleConfirmStatus = () => {
    if (!clinic) return;
    setStatus(offersActivate ? ClinicStatus.Active : ClinicStatus.Inactive);
  };

  if (isLoading) {
    return (
      <div className="cdp">
        <div className="cdp-loading">
          <div className="loading-spinner" />
          <p>Loading clinic details...</p>
        </div>
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className="cdp">
        <div className="cdp-error">
          <p>{(error as Error)?.message || 'Error loading clinic details'}</p>
          <Link to="/clinics" className="btn-secondary">Back to Clinics</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cdp">
      {/* Breadcrumb */}
      <div className="cdp-breadcrumb">
        <button type="button" className="cdp-breadcrumb-link" onClick={handleBackToGrid}>
          Clinics
        </button>
        <ChevronRight size={14} className="cdp-breadcrumb-sep" />
        <span className="cdp-breadcrumb-current">{clinic.name}</span>
      </div>

      {/* Header */}
      <div className="cdp-header">
        <div className="cdp-header-main">
          <div className="cdp-header-left">
            <div className="cdp-name-row">
              <h1 className="cdp-name">{clinic.name}</h1>
              <span className={`status-badge ${clinic.statusCssClass}`}>
                {clinic.statusDisplay}
              </span>
              {clinic.lastVerifiedDate &&
                (new Date().getTime() - new Date(clinic.lastVerifiedDate).getTime()) < 30 * 24 * 60 * 60 * 1000 && (
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                  Verified ✓
                </span>
              )}
            </div>
            <div className="cdp-identifiers">
              <span>ID: {clinic.id}</span>
              {clinic.code && (
                <>
                  <span className="cdp-id-sep">|</span>
                  <span>Code: {clinic.code}</span>
                </>
              )}
            </div>
            {clinic.lastVerifiedDate && (
              <div className="cdp-verification-info">
                Last Verified: {new Date(clinic.lastVerifiedDate).toLocaleDateString()}
                {clinic.lastVerifiedByUserName && ` by ${clinic.lastVerifiedByUserName}`}
              </div>
            )}
          </div>
          <div className="cdp-header-right">
            {canVerify && (
              <RequirePermission permission="UserCanCompleteClinicVerification">
                <button
                  type="button"
                  className="cdp-verify-btn"
                  onClick={handleOpenVerify}
                  disabled={verifyPending}
                >
                  <CheckCircle size={16} />
                  {verifyPending ? 'Verifying...' : 'Verify Clinic'}
                </button>
              </RequirePermission>
            )}
            <RequirePermission permission="UserCanEditClinicProfile">
              <button
                type="button"
                className={`cdp-status-btn ${offersActivate ? 'cdp-status-btn--activate' : 'cdp-status-btn--deactivate'}`}
                onClick={handleOpenStatus}
                disabled={statusPending}
              >
                <Power size={16} />
                {offersActivate ? 'Activate' : 'Deactivate'}
              </button>
            </RequirePermission>
          </div>
        </div>

        {/* Contact Info Bar */}
        <div className="cdp-contact-bar">
          <div className="cdp-contact-item">
            <MapPin size={14} />
            <span>{clinic.fullAddress || 'No address'}</span>
          </div>
          {clinic.phone && (
            <div className="cdp-contact-item">
              <Phone size={14} />
              <PhoneLink phone={clinic.phone} />
            </div>
          )}
          {clinic.fax && (
            <div className="cdp-contact-item">
              <Printer size={14} />
              <PhoneDisplay phone={clinic.fax} />
            </div>
          )}
          {clinic.email && (
            <div className="cdp-contact-item">
              <Mail size={14} />
              <a href={`mailto:${clinic.email}`}>{clinic.email}</a>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="cdp-tabs">
        <button
          type="button"
          className={`cdp-tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          <FileText size={16} />
          <span>Info</span>
        </button>
        <button
          type="button"
          className={`cdp-tab ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contacts')}
        >
          <Users size={16} />
          <span>Contacts</span>
        </button>
        <button
          type="button"
          className={`cdp-tab ${activeTab === 'addresses' ? 'active' : ''}`}
          onClick={() => setActiveTab('addresses')}
        >
          <MapPin size={16} />
          <span>Addresses</span>
        </button>
        <button
          type="button"
          className={`cdp-tab ${activeTab === 'services' ? 'active' : ''}`}
          onClick={() => setActiveTab('services')}
        >
          <Stethoscope size={16} />
          <span>Services</span>
        </button>
      </div>

      {/* Tab Content */}
      <div className="cdp-content">
        {activeTab === 'info' && (
          <div className="cdp-info-sections">
            <ClinicInfoSection clinic={clinic} onUpdate={refetch} />
            <OperatingHoursSection clinic={clinic} onUpdate={refetch} />
            <ClinicCategoriesSection clinic={clinic} onUpdate={refetch} />
            <ClinicCapabilitiesSection clinic={clinic} onUpdate={refetch} />
            <ClinicOnboardingSection clinic={clinic} onUpdate={refetch} />
            <ClinicExternalSystemsSection clinic={clinic} onUpdate={refetch} />
          </div>
        )}
        {activeTab === 'contacts' && <ClinicContactsView clinicId={clinicId} />}
        {activeTab === 'addresses' && <ClinicAddressesTab clinic={clinic} />}
        {activeTab === 'services' && <ClinicProceduresView clinicId={clinicId} />}
      </div>

      {/* Verify Confirmation Modal */}
      {verifyModal.isOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={verifyModal.close} onKeyDown={(e) => { if (e.key === 'Escape') verifyModal.close(); }}>
          <div className="modal-content verify-modal" role="document" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <h2>Verify Clinic</h2>
            <p>
              Are you sure you want to mark <strong>{clinic.name}</strong> as verified?
            </p>
            <p className="modal-description">
              This will update the last verified date to the current time and record you as the verifier.
            </p>

            {verifyError && (
              <div className="modal-error">
                <AlertCircle size={16} />
                {verifyError}
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { verifyModal.close(); clearVerifyError(); }}
                disabled={verifyPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConfirmVerify}
                disabled={verifyPending}
              >
                <CheckCircle size={16} />
                {verifyPending ? 'Verifying...' : 'Confirm Verification'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activate/Deactivate Confirmation Modal */}
      {statusModal.isOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={statusModal.close} onKeyDown={(e) => { if (e.key === 'Escape') statusModal.close(); }}>
          <div className="modal-content verify-modal" role="document" onClick={(e) => e.stopPropagation()}>
            <h2>{offersActivate ? 'Activate Clinic' : 'Deactivate Clinic'}</h2>
            <p>
              Are you sure you want to {offersActivate ? 'activate' : 'deactivate'} <strong>{clinic.name}</strong>?
            </p>
            <p className="modal-description">
              {offersActivate
                ? (clinic.status === ClinicStatus.Draft
                    ? 'This clinic will be marked Active and become available for use.'
                    : 'This clinic will be marked Active and will be available for use again.')
                : 'This clinic will be marked Inactive and will no longer appear as an active clinic. You can reactivate it later from this screen.'}
            </p>

            {statusError && (
              <div className="modal-error">
                <AlertCircle size={16} />
                {statusError}
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { statusModal.close(); clearStatusError(); }}
                disabled={statusPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`btn ${offersActivate ? 'btn-primary' : 'btn-danger'}`}
                onClick={handleConfirmStatus}
                disabled={statusPending}
              >
                <Power size={16} />
                {statusPending
                  ? (offersActivate ? 'Activating...' : 'Deactivating...')
                  : (offersActivate ? 'Confirm Activation' : 'Confirm Deactivation')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
